import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ArticleCard from '../components/ArticleCard';
import Sidebar from '../components/Sidebar';
import { useLanguage } from '../context/LanguageContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CategoryPage({ isTag = false }) {
  const { slug, tag } = useParams();
  const { lang } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [recent, setRecent] = useState([]);
  const [popular, setPopular] = useState([]);
  const [tags, setTags] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState('');
  const limit = 12;

  const identifier = isTag ? tag : slug;
  const title = isTag ? `#${tag}` : (categoryName || slug?.charAt(0).toUpperCase() + slug?.slice(1));

  useEffect(() => {
    loadArticles(1);
    loadSidebar();
    setPage(1);
    if (!isTag && slug) {
      axios.get(`${API}/api/categories`).then(r => {
        const cats = r.data?.categories || [];
        const match = cats.find(c => c.slug === slug);
        if (match) setCategoryName(match.name);
      }).catch(() => {});
    }
  }, [slug, tag, isTag]);

  const loadArticles = async (p) => {
    setLoading(true);
    try {
      const endpoint = isTag
        ? `${API}/api/articles?tag=${encodeURIComponent(identifier)}&page=${p}&limit=${limit}&status=published&lang=${lang}`
        : `${API}/api/articles?category=${identifier}&page=${p}&limit=${limit}&status=published&lang=${lang}`;
      const res = await axios.get(endpoint);
      setArticles(res.data?.articles || res.data || []);
      setTotal(res.data?.total || (res.data?.length ? res.data.length : 0));
    } catch { setArticles([]); }
    finally { setLoading(false); }
  };

  const loadSidebar = async () => {
    try {
      const [r, p, t] = await Promise.all([
        axios.get(`${API}/api/articles?limit=5&status=published&lang=${lang}`),
        axios.get(`${API}/api/articles?limit=5&sort=views&status=published&lang=${lang}`),
        axios.get(`${API}/api/tags?limit=30`),
      ]);
      setRecent(r.data?.articles || r.data || []);
      setPopular(p.data?.articles || p.data || []);
      setTags(t.data?.tags || t.data || []);
    } catch {}
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="min-h-screen bg-cream dark:bg-gray-900">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Category Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <span className="w-1 h-6 bg-terracotta rounded-sm" />
            <span className="text-xs font-accent uppercase tracking-widest text-terracotta">{isTag ? (lang === 'hr' ? 'Oznaka' : 'Tag') : (lang === 'hr' ? 'Kategorija' : 'Category')}</span>
          </div>
          <h1 className="font-serif text-3xl font-bold text-navy dark:text-white">{title}</h1>
          <p className="text-gray-500 text-sm mt-1">{total} {lang === 'hr' ? 'članaka' : 'articles'}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-3">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {[...Array(6)].map((_, i) => <div key={i} className="skeleton aspect-[4/3] rounded-xl" />)}
              </div>
            ) : articles.length > 0 ? (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {articles.map(article => (
                    <ArticleCard key={article.id || article._id} article={article} size="medium" />
                  ))}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <button disabled={page === 1} onClick={() => { setPage(p => p-1); loadArticles(page-1); }} className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                      <ChevronLeft size={16} />
                    </button>
                    {[...Array(Math.min(5, totalPages))].map((_, i) => (
                      <button key={i+1} onClick={() => { setPage(i+1); loadArticles(i+1); }} className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${page === i+1 ? 'bg-navy text-white' : 'border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                        {i+1}
                      </button>
                    ))}
                    <button disabled={page === totalPages} onClick={() => { setPage(p => p+1); loadArticles(page+1); }} className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-400 font-serif text-xl">{lang === 'hr' ? `Nema članaka u ovoj ${isTag ? 'oznaci' : 'kategoriji'}.` : `No articles found in this ${isTag ? 'tag' : 'category'} yet.`}</p>
              </div>
            )}
          </div>
          <div className="lg:col-span-1">
            <div className="sticky top-20">
              <Sidebar recentArticles={recent} popularArticles={popular} tags={tags} />
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
